// Base de conocimiento del ChatBot - Rumbo Punilla con IA Mejorada

export interface KnowledgeEntry {
  keywords: string[];
  category: string;
  response: string;
  followUp?: string[];
  priority?: number; // Mayor número = mayor prioridad en coincidencias
  patterns?: RegExp[]; // Expresiones regulares para detección avanzada
  synonyms?: string[]; // Sinónimos para mejorar detección
}

// Función auxiliar para normalizar texto (eliminar acentos y normalizar)
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
    .replace(/[^\w\s]/g, " ") // Reemplazar caracteres especiales con espacios
    .replace(/\s+/g, " ") // Normalizar espacios múltiples
    .trim();
}

// Función para calcular similitud entre textos (algoritmo de Jaccard mejorado)
function calculateSimilarity(text1: string, text2: string): number {
  const words1 = new Set(normalizeText(text1).split(" ").filter(w => w.length > 2));
  const words2 = new Set(normalizeText(text2).split(" ").filter(w => w.length > 2));

  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
}

// Función para detectar preguntas
function isQuestion(text: string): boolean {
  const questionWords = ["como", "cuando", "donde", "que", "cual", "quien", "por que", "puedo", "debo", "hay"];
  const normalizedText = normalizeText(text);
  return questionWords.some(q => normalizedText.includes(q)) || text.includes("?");
}

// Función para detectar negativos
function hasNegative(text: string): boolean {
  const negativeWords = ["no puedo", "no funciona", "no aparece", "no se", "no esta", "problema", "error", "falla"];
  const normalizedText = normalizeText(text);
  return negativeWords.some(n => normalizedText.includes(n));
}

export const knowledgeBase: KnowledgeEntry[] = [
  // SOPORTE TÉCNICO
  {
    keywords: ["soporte", "ayuda tecnica", "problema tecnico", "error", "plataforma", "asistencia"],
    synonyms: ["ayuda", "apoyo", "servicio", "atencion"],
    category: "Soporte Técnico",
    priority: 5,
    response: "Estoy aquí para ayudarte. Para soporte técnico puedes:\n\n📞 Teléfono: +54 9 351 683 7321 (9:00-20:00 hs)\n💬 WhatsApp: http://wa.me/5493516837321\n📧 Email: rumbo.punilla@gmail.com\n\nTiempo de respuesta: 24 horas hábiles.",
    followUp: ["¿Qué tipo de problema técnico tienes?", "¿Necesitas ayuda con tu cuenta?"]
  },
  {
    keywords: ["no puedo acceder", "cuenta bloqueada", "contraseña", "login", "ingresar", "entrar", "iniciar sesion"],
    synonyms: ["loguearse", "autenticarse", "password"],
    category: "Soporte Técnico",
    priority: 8,
    patterns: [/no\s+(puedo|logro|consigo)\s+(acceder|entrar|ingresar|logear)/i],
    response: "Si no puedes acceder a tu cuenta, intenta:\n\n1. 🔑 Restablecer la contraseña desde el login\n2. 🗑️ Borrar caché y cookies del navegador\n3. 🌐 Probar desde otro navegador o dispositivo\n4. ✅ Verificar que tu email esté bien escrito\n\nSi el problema persiste, contáctanos a rumbo.punilla@gmail.com con tu email registrado.",
  },
  {
    keywords: ["cargar fotos", "subir imagenes", "imagen", "foto", "archivo", "fotografia"],
    synonyms: ["pic", "picture", "jpg", "png"],
    category: "Soporte Técnico",
    priority: 7,
    patterns: [/no\s+(puedo|logro|se)\s+(cargar|subir|agregar)\s+(foto|imagen)/i],
    response: "Para cargar fotos correctamente:\n\n✅ Formato: JPG o PNG\n✅ Tamaño máximo: 5MB por foto\n✅ Usa imágenes de alta calidad con buena iluminación\n✅ Verifica que no estén corruptas\n\n💡 Tip: Si el archivo es muy grande, comprime la imagen manteniendo calidad.\n\nSi persiste el problema, revisa tu conexión a internet.",
  },
  {
    keywords: ["anuncio no aparece", "busqueda", "no encuentro mi propiedad", "no veo", "visibilidad", "invisible"],
    synonyms: ["publicacion", "listado"],
    category: "Soporte Técnico",
    priority: 8,
    patterns: [/mi\s+anuncio\s+no\s+(aparece|sale|muestra|se ve)/i],
    response: "Si tu anuncio no aparece en búsquedas:\n\n1. ✅ Verifica que la disponibilidad esté configurada\n2. 📍 Revisa que la ubicación sea correcta\n3. 📸 Asegúrate de tener al menos 5 fotos de calidad\n4. ✍️ Completa toda la descripción al 100%\n5. 💰 Confirma que el precio esté establecido\n6. 🟢 Verifica que el estado sea 'Activo'\n\nLos anuncios completos tienen 5x mejor visibilidad.",
  },
  {
    keywords: ["editar anuncio", "modificar publicacion", "actualizar", "cambiar datos"],
    category: "Soporte Técnico",
    priority: 6,
    patterns: [/(como|donde|puedo)\s+(editar|modificar|cambiar)\s+(mi\s+)?(anuncio|publicacion)/i],
    response: "Para editar tu anuncio:\n\n1. Ve a 'Mis Alojamientos' o 'Mis Excursiones'\n2. Selecciona la propiedad que quieres editar\n3. Haz clic en el botón 'Editar'\n4. Modifica la información que necesites\n5. Guarda los cambios\n\nPuedes editar: fotos, descripción, precios, disponibilidad, servicios y normas.",
  },

  // PROBLEMAS CON ALOJAMIENTO
  {
    keywords: ["problema alojamiento", "sucio", "mal estado", "no cumple", "reclamo", "queja", "insatisfecho"],
    synonyms: ["descontento", "molesto", "enojado"],
    category: "Problemas Alojamiento",
    priority: 9,
    patterns: [/el\s+alojamiento\s+(esta|no)\s+(sucio|limpio|mal|bien)/i],
    response: "Si el alojamiento no cumple lo prometido:\n\n1. 📞 Contacta al anfitrión de inmediato\n2. 📸 Documenta con fotos y capturas\n3. 🤝 Busca una solución directa primero\n4. 📋 Repórtalo en la plataforma si no hay solución\n5. ⭐ Deja una reseña honesta después\n\n⚠️ Importante: La plataforma no intermedia en reembolsos, es un acuerdo directo entre viajero y anfitrión.",
  },
  {
    keywords: ["anfitrion no responde", "check-in", "no esta", "no contesta", "no llega", "ausente"],
    synonyms: ["dueno", "propietario", "host"],
    category: "Problemas Alojamiento",
    priority: 10,
    patterns: [/anfitrion\s+(no|sin)\s+(responde|contesta|aparece|esta)/i],
    response: "Si el anfitrión no responde:\n\n1. 📧 Revisa si dejó instrucciones previas por email o WhatsApp\n2. 📱 Intenta contactarlo por TODOS los medios: chat, teléfono, WhatsApp\n3. ⏰ Espera al menos 30-60 minutos\n4. 🏨 Si no hay respuesta para el check-in, considera otra opción\n5. 📋 Repórtalo urgentemente en la plataforma\n\n🚫 La plataforma no puede relocalizar viajeros ni garantizar respuesta inmediata.",
  },
  {
    keywords: ["wifi", "agua", "luz", "calefaccion", "aire acondicionado", "no funciona", "servicio", "roto", "falla"],
    synonyms: ["internet", "electricidad", "climatizacion"],
    category: "Problemas Alojamiento",
    priority: 8,
    patterns: [/(wifi|agua|luz|calefaccion|aire)\s+(no|sin)\s+(funciona|anda|hay)/i],
    response: "Si hay servicios que no funcionan:\n\n1. 📞 Informa al anfitrión INMEDIATAMENTE\n2. 🔌 Verifica si hay cortes generales en la zona\n3. ⏰ Da tiempo razonable (2-4 horas) para que lo solucione\n4. 📸 Documenta el problema con fotos\n5. 📋 Si no se resuelve en tiempo razonable, puedes solicitar cancelación\n\nEl anfitrión es 100% responsable de estos servicios básicos.",
  },
  {
    keywords: ["inseguro", "seguridad", "peligro", "miedo", "robo", "amenaza"],
    category: "Problemas Alojamiento",
    priority: 10,
    response: "🚨 Si te sientes inseguro:\n\n**PRIORIDAD: Tu seguridad es lo primero**\n\n1. 🏃 Aléjate del lugar INMEDIATAMENTE\n2. 🚓 Contacta autoridades locales si hay peligro real\n3. 📱 Llama a un familiar o amigo\n4. 📋 Reporta el caso en la plataforma URGENTEMENTE\n5. 📸 Documenta la situación si es seguro hacerlo\n\nRevisaremos y tomaremos medidas con el anfitrión.",
  },
  {
    keywords: ["devolver dinero", "recuperar pago", "reintegro"],
    category: "Problemas Alojamiento",
    priority: 7,
    response: "Sobre devoluciones de dinero:\n\n⚠️ La plataforma NO procesa reembolsos directamente.\n\nDebes:\n1. 💬 Negociar con el anfitrión directamente\n2. 📋 Revisar las políticas de cancelación aplicables\n3. 💳 El anfitrión gestiona el reembolso\n\nSi hay incumplimiento grave, repórtalo para evaluar el caso.",
  },

  // POLÍTICAS DE CANCELACIÓN
  {
    keywords: ["cancelar", "cancelacion", "anular", "deshacer reserva"],
    synonyms: ["abortar", "suspender"],
    category: "Cancelaciones",
    priority: 9,
    patterns: [/(como|quiero|puedo)\s+(cancelar|anular)\s+(mi\s+)?reserva/i],
    response: "**Política de Cancelación para Huéspedes:**\n\n✅ **7+ días antes**: 100% reembolso (excepto tarifas de servicio)\n⚠️ **6-3 días antes**: 50% reembolso\n❌ **Menos de 72 horas**: Sin reembolso\n\n🏥 **Fuerza mayor**: Se evalúan casos con documentación (enfermedad, desastres, restricciones).\n\nPara cancelar: Ve a 'Mis Reservas' → Selecciona reserva → 'Cancelar'",
  },
  {
    keywords: ["reembolso", "devolucion", "recuperar plata", "dinero de vuelta"],
    synonyms: ["devolver", "restituir"],
    category: "Cancelaciones",
    priority: 8,
    patterns: [/(cuando|como)\s+(me\s+)?(devuelven|reembolsan|reintegran)/i],
    response: "**Tiempos de Reembolso:**\n\n💳 **Tarjeta de crédito**: 5-10 días hábiles\n💰 **Transferencia**: 3-7 días hábiles\n\n📋 **Requisitos**:\n- Cancelación dentro de los plazos establecidos\n- El anfitrión procesa el reembolso\n- Según política de cancelación aplicable\n\n⚠️ Las tarifas de servicio NO se reembolsan.",
  },
  {
    keywords: ["modificar reserva", "cambiar fecha", "reprogramar", "mover reserva"],
    category: "Cancelaciones",
    priority: 7,
    patterns: [/(puedo|quiero)\s+(cambiar|modificar|mover)\s+(la\s+)?fecha/i],
    response: "Para modificar tu reserva:\n\n✅ **Con más de 5 días**: Posible según disponibilidad del anfitrión\n⚠️ **Con menos de 5 días**: A discreción del anfitrión\n\n💡 **Proceso**:\n1. Contacta al anfitrión por chat\n2. Coordina nuevas fechas\n3. Verifica disponibilidad\n4. Puede haber ajustes de tarifa\n\n📝 Es más fácil que cancelar y reservar de nuevo.",
  },
  {
    keywords: ["anfitrion cancela", "cancelacion del dueno", "me cancelaron"],
    category: "Cancelaciones",
    priority: 10,
    patterns: [/el\s+anfitrion\s+(cancelo|me\s+cancela)/i],
    response: "Si el anfitrión cancela tu reserva:\n\n✅ **Debe ofrecer**:\n1. Alojamiento alternativo similar en la plataforma\n2. O reembolso del 100% del monto pagado\n\n⚠️ **Penalizaciones para el anfitrión**:\n- Paga 10% del total de la reserva\n- Cancelaciones reiteradas = suspensión de perfil\n\nLa plataforma velará por tu reserva confirmada.",
  },

  // PRIVACIDAD Y TÉRMINOS
  {
    keywords: ["privacidad", "datos personales", "informacion personal", "seguridad datos", "proteccion"],
    category: "Privacidad",
    priority: 6,
    patterns: [/(que|cuales)\s+datos\s+(recopilan|guardan|tienen)/i],
    response: "**Tu privacidad es importante:**\n\n🔒 **Recopilamos**:\n- Nombre, email, teléfono\n- Datos de navegación (IP, dispositivo)\n- Historial de reservas\n\n✅ **Protección**:\n- NO almacenamos datos financieros\n- NO vendemos tus datos a terceros\n- Solo compartimos cuando la ley lo requiere\n- Encriptación de datos sensibles\n\nTu información está protegida y segura.",
  },
  {
    keywords: ["responsabilidad", "quien responde", "problema pago", "disputa", "conflicto"],
    category: "Términos de Uso",
    priority: 7,
    response: "**Responsabilidad de la Plataforma:**\n\n🔹 **Somos intermediarios**, NO somos:\n❌ Propietarios de alojamientos\n❌ Administradores de propiedades\n❌ Mediadores en disputas de pago\n❌ Garantes de calidad\n\n✅ **Los anfitriones son responsables de**:\n- Calidad del alojamiento\n- Cumplir con reservas confirmadas\n- Gestionar cancelaciones y reembolsos\n- Servicios y amenities prometidos",
  },

  // RECURSOS PARA ANFITRIONES
  {
    keywords: ["crear anuncio", "publicar propiedad", "nuevo alojamiento", "subir casa", "agregar departamento"],
    synonyms: ["cargar", "anadir"],
    category: "Recursos Anfitrión",
    priority: 8,
    patterns: [/(como|donde|quiero)\s+(crear|hacer|publicar|subir)\s+(un\s+)?(anuncio|propiedad|alojamiento)/i],
    response: "**Para crear un anuncio exitoso:**\n\n📝 **Título claro**: 'Cabaña con jacuzzi en Villa Giardino'\n📸 **Mínimo 5 fotos** de alta calidad\n✍️ **Descripción completa**: Servicios, ubicación, normas\n💰 **Precio competitivo** (investiga tu zona)\n📅 **Disponibilidad** clara\n🏠 **Tipo de propiedad** correcto\n📋 **Reglas claras**: Check-in, mascotas, fiestas\n\n💡 ¿Necesitas ayuda paso a paso?",
    followUp: ["¿Quieres consejos sobre fotos?", "¿Necesitas ayuda con precios?"]
  },
  {
    keywords: ["optimizar anuncio", "mejorar publicacion", "mas reservas", "aumentar visibilidad", "vender mas"],
    category: "Optimizar Anuncio",
    priority: 9,
    patterns: [/(como|quiero)\s+(mejorar|optimizar|aumentar)\s+(mi\s+)?(anuncio|visibilidad|reservas)/i],
    response: "**Guía para Optimizar tu Anuncio:**\n\n📸 **Fotos profesionales**\n- Luz natural (mañana 9-11am)\n- Ángulo amplio, espacio ordenado\n- Mínimo 8-10 fotos\n\n📝 **Título atractivo**\n- ✅ 'Loft moderno con terraza y parrilla'\n- ❌ 'Departamento en alquiler'\n\n✨ **Destaca lo único**\n- Jacuzzi, vista, pileta, WiFi rápido\n- Pet-friendly, cerca del centro\n\n💰 **Estrategias**\n- Descuentos estadías largas (10-15%)\n- Precio dinámico (alta/baja)\n\n⭐ **Responde rápido**\n- En menos de 1 hora = prioridad\n\n¡Anuncios completos reciben 5x más reservas!",
  },
  {
    keywords: ["fotos profesionales", "imagenes", "fotografia", "galeria", "que fotos subir"],
    synonyms: ["pics", "pictures"],
    category: "Optimizar Anuncio",
    priority: 7,
    patterns: [/(que|cuales|como)\s+(fotos|imagenes)\s+(subir|sacar|poner)/i],
    response: "**Guía para Fotos Perfectas:**\n\n✅ **Técnica**:\n- Luz natural (9-11am o 4-6pm)\n- Espacios limpios y despejados\n- Ángulo desde la esquina (más espacio)\n- Sin filtros exagerados\n- Resolución mínima: 1920x1080px\n\n📸 **Galería ideal (mínimo 8 fotos)**:\n1. 🏠 Foto destacada: Fachada o vista general\n2. 🛏️ 2-3 Dormitorio(s) desde distintos ángulos\n3. 🛋️ 2 Sala de estar/comedor\n4. 🍳 2 Cocina equipada\n5. 🚿 1-2 Baño(s)\n6. 🌳 2-3 Áreas exteriores (jardín, terraza, pileta)\n7. ✨ 1 Detalle especial (chimenea, vista, jacuzzi)\n\n💡 Tip: Contrata un fotógrafo si puedes ($50-100 USD), vale la pena.",
  },
  {
    keywords: ["precio justo", "tarifa", "cobrar", "cuanto vale", "establecer precio"],
    synonyms: ["costo", "valor"],
    category: "Recursos Anfitrión",
    priority: 8,
    patterns: [/(cuanto|como)\s+(cobrar|cobro|precio|poner|vale)/i],
    response: "**Estrategia de Precios Ganadora:**\n\n📊 **Investigación**:\n1. Busca alojamientos similares en tu zona\n2. Analiza: tamaño, servicios, ubicación\n3. Observa temporadas altas/bajas\n\n💰 **Descuentos que funcionan**:\n- 🏖️ Estadías +3 noches: 10-15% OFF\n- 📅 Reserva anticipada (+30 días): 5-10% OFF\n- ⚡ Último minuto (<7 días): 15-20% OFF\n- 📆 Temporada baja: 20-30% OFF\n\n💡 **Consejos**:\n- Precio alto + descuentos > Precio bajo fijo\n- Actualiza según demanda\n- Considera costos: limpieza, servicios, impuestos\n\n¡Precio competitivo = más reservas!",
  },
  {
    keywords: ["gestion reservas", "confirmar", "administrar", "huespedes", "comunicacion"],
    category: "Recursos Anfitrión",
    priority: 7,
    response: "**Gestión de Reservas Efectiva:**\n\n✅ **Confirmación rápida**\n- Responde en menos de 24 horas\n- Confirma o rechaza (no dejes pendiente)\n\n📅 **Calendario actualizado**\n- Bloquea fechas ocupadas inmediatamente\n- Usa sincronización si publicas en otras plataformas\n\n💬 **Comunicación profesional**\n- Tono amable y claro\n- Responde todas las dudas\n- Envía instrucciones de check-in 24-48hs antes\n\n📋 **Instrucciones claras**\n- Cómo llegar con GPS\n- Dónde estacionar\n- Código/llave de acceso\n- WiFi y servicios\n\n⭐ **Post-estadía**\n- Pide reseña amablemente\n- Agradece la visita",
  },
  {
    keywords: ["reseñas buenas", "comentarios", "calificacion", "opiniones", "5 estrellas", "reputacion"],
    synonyms: ["reviews", "rating"],
    category: "Recursos Anfitrión",
    priority: 7,
    patterns: [/(como|quiero)\s+(mejorar|obtener|conseguir)\s+(resenas|calificacion|estrellas)/i],
    response: "**Mejora tu Reputación y Consigue 5 ⭐:**\n\n🏠 **Alojamiento impecable**\n- Limpieza PROFUNDA entre cada estadía\n- Todo funciona: WiFi, agua, luz, clima\n- Mantén decoración agradable\n\n🎁 **Detalles que marcan**\n- Bienvenida personalizada (nota, snacks)\n- Guía local (restaurantes, paseos)\n- Artículos básicos (shampoo, café, papel)\n\n💬 **Pide retroalimentación**\n- Al finalizar estadía (no inmediatamente)\n- Mensaje amable: 'Sería un honor si dejas una reseña'\n\n✍️ **Responde TODAS las reseñas**\n- Positivas: Agradece sinceramente\n- Negativas: Discúlpate y explica mejoras\n- Nunca te pongas a la defensiva\n\n⭐ **5 estrellas = Top en búsquedas = Más reservas**",
  },
  {
    keywords: ["normas casa", "reglas", "politicas", "que permitir"],
    category: "Recursos Anfitrión",
    priority: 6,
    response: "**Normas de la Casa Recomendadas:**\n\n🚭 **No fumar**: Especifica dónde sí pueden (terraza, jardín)\n🎉 **Fiestas**: Permitidas o prohibidas\n🐶 **Mascotas**: Sí/No, tamaño máximo, costo extra\n👥 **Huéspedes máximos**: Según capacidad real\n⏰ **Horarios**:\n- Check-in: 14:00-20:00 hs\n- Check-out: 10:00-11:00 hs\n- Silencio: 22:00-8:00 hs\n\n📋 **Otras**:\n- No hacer ruido excesivo\n- Dejar limpio y ordenado\n- Cuidar muebles y equipamiento\n\n💡 Sé claro desde el principio para evitar problemas.",
  },

  // PREGUNTAS FRECUENTES MEJORADAS
  {
    keywords: ["faq", "preguntas frecuentes", "dudas", "ayuda general", "consultas"],
    category: "FAQs",
    priority: 5,
    response: "**Preguntas Frecuentes Top:**\n\n❓ ¿Cómo accedo a mi cuenta?\n❓ ¿Cómo edito mi anuncio?\n❓ ¿Cómo cambio precios o disponibilidad?\n❓ ¿Cómo respondo mensajes de huéspedes?\n❓ ¿Cuáles son las políticas de cancelación?\n❓ ¿Qué hago si tengo problemas con el alojamiento?\n❓ ¿Cómo optimizo mi anuncio?\n❓ ¿Cómo funciona el sistema de pagos?\n\n¿Sobre cuál de estos temas necesitas información detallada?",
  },
  {
    keywords: ["como funciona", "que es", "explicacion", "entender"],
    category: "FAQs",
    priority: 4,
    patterns: [/(como|que)\s+(funciona|es)\s+(la\s+)?plataforma/i],
    response: "**Cómo funciona Rumbo Punilla:**\n\n🏠 **Para Anfitriones**:\n1. Crea tu anuncio con fotos y descripción\n2. Establece precios y disponibilidad\n3. Recibes solicitudes de reserva\n4. Confirmas o rechazas\n5. Recibes el pago\n\n✈️ **Para Viajeros**:\n1. Busca alojamientos por ubicación y fechas\n2. Compara precios y servicios\n3. Haces la reserva y pagas\n4. El anfitrión confirma\n5. Disfrutas tu estadía\n\n💳 **Pagos seguros** procesados por la plataforma\n⭐ **Sistema de reseñas** para confianza mutua",
  },
  {
    keywords: ["contacto", "hablar con alguien", "atencion al cliente", "telefono", "email"],
    category: "FAQs",
    priority: 8,
    patterns: [/(como|quiero)\s+(contactar|hablar|comunicar)\s+(con|a)/i],
    response: "**Contacto Rumbo Punilla:**\n\n📞 **Teléfono**: +54 9 351 683 7321\n⏰ Horario: Lunes a Domingo 9:00-20:00 hs\n\n💬 **WhatsApp**: http://wa.me/5493516837321\n(Respuesta más rápida)\n\n📧 **Email**: rumbo.punilla@gmail.com\n⏱️ Tiempo respuesta: 24 horas hábiles\n\n💻 **Chat de soporte**: ¡Estás usándolo ahora!\n\n¿Prefieres que te ayude yo o necesitas contacto directo?",
  },
  {
    keywords: ["whatsapp", "wsp", "wa", "mensaje directo", "chat directo"],
    synonyms: ["watts", "watsap", "guasap"],
    category: "Contacto",
    priority: 10,
    patterns: [/whatsapp|wsp|wa\.me/i],
    response: "**Contáctanos por WhatsApp:**\n\n💬 Haz clic aquí para chatear directamente: http://wa.me/5493516837321\n\n⏰ Horario de atención:\nLunes a Domingo 9:00-20:00 hs\n\n✅ Respuesta más rápida garantizada\n✅ Soporte en tiempo real\n✅ Envía capturas de pantalla fácilmente\n\n¡Te esperamos!",
  },

  // SALUDOS Y CONVERSACIÓN NATURAL
  {
    keywords: ["hola", "buenos dias", "buenas tardes", "buenas noches", "hey", "ola", "saludos"],
    category: "Saludo",
    priority: 10,
    response: "¡Hola! 👋 Soy el asistente virtual de Rumbo Punilla.\n\n¿En qué puedo ayudarte hoy?\n\n• 🛠️ Soporte técnico\n• 🏠 Problemas con alojamiento\n• ❌ Cancelaciones y reembolsos\n• 📋 Recursos para anfitriones\n• ⭐ Optimizar anuncios\n• 🔒 Políticas y privacidad\n\nEscribe tu consulta y te responderé al instante.",
  },
  {
    keywords: ["gracias", "muchas gracias", "perfecto", "excelente", "genial", "ok"],
    category: "Agradecimiento",
    priority: 10,
    response: "¡De nada! 😊 Estoy aquí para ayudarte siempre que lo necesites.\n\n¿Hay algo más en lo que pueda asistirte?",
  },
  {
    keywords: ["adios", "chau", "hasta luego", "nos vemos", "bye", "cierro"],
    category: "Despedida",
    priority: 10,
    response: "¡Hasta pronto! 👋\n\nSi necesitas ayuda en el futuro, aquí estaré.\n\n¡Que tengas un excelente día y buenas reservas! 🏡✨",
  },
  {
    keywords: ["quien eres", "que eres", "bot", "robot", "ia", "inteligencia artificial"],
    category: "Sobre el Bot",
    priority: 9,
    patterns: [/(quien|que)\s+(eres|sos|es)/i],
    response: "¡Hola! Soy el asistente virtual inteligente de Rumbo Punilla 🤖\n\nEstoy programado para ayudarte con:\n✅ Resolver dudas sobre la plataforma\n✅ Problemas técnicos\n✅ Políticas y procedimientos\n✅ Consejos para optimizar anuncios\n✅ Guías para anfitriones\n\nMis respuestas están basadas en la documentación oficial de Rumbo Punilla.\n\n¿En qué puedo ayudarte específicamente?",
  },
];

// Función mejorada para encontrar la mejor respuesta
export function findBestResponse(userMessage: string): KnowledgeEntry | null {
  if (!userMessage || userMessage.trim().length < 2) return null;

  const normalizedMessage = normalizeText(userMessage);
  //const messageWords = normalizedMessage.split(" ").filter(w => w.length > 2);

  // Array para almacenar coincidencias con puntuación
  interface ScoredEntry {
    entry: KnowledgeEntry;
    score: number;
  }
  const matches: ScoredEntry[] = [];

  for (const entry of knowledgeBase) {
    let score = 0;
    const priority = entry.priority || 1;

    // 1. Buscar coincidencias con patrones regex (máxima prioridad)
    if (entry.patterns) {
      for (const pattern of entry.patterns) {
        if (pattern.test(userMessage)) {
          score += 100 * priority;
          break;
        }
      }
    }

    // 2. Buscar coincidencias exactas en keywords
    for (const keyword of entry.keywords) {
      const normalizedKeyword = normalizeText(keyword);
      if (normalizedMessage.includes(normalizedKeyword)) {
        score += 50 * priority;
      }
    }

    // 3. Buscar coincidencias en sinónimos
    if (entry.synonyms) {
      for (const synonym of entry.synonyms) {
        const normalizedSynonym = normalizeText(synonym);
        if (normalizedMessage.includes(normalizedSynonym)) {
          score += 30 * priority;
        }
      }
    }

    // 4. Calcular similitud de palabras
    const allKeywords = [...entry.keywords, ...(entry.synonyms || [])].join(" ");
    const similarity = calculateSimilarity(normalizedMessage, allKeywords);
    score += similarity * 20 * priority;

    // 5. Bonus por tipo de pregunta
    if (isQuestion(userMessage) && entry.followUp) {
      score += 10;
    }

    // 6. Bonus por detección de negativo en problemas
    if (hasNegative(userMessage) && entry.category.includes("Problema")) {
      score += 15;
    }

    if (score > 0) {
      matches.push({ entry, score });
    }
  }

  // Ordenar por puntuación y devolver la mejor
  if (matches.length > 0) {
    matches.sort((a, b) => b.score - a.score);

    // Solo devolver si la puntuación es significativa (evitar falsos positivos)
    if (matches[0].score >= 20) {
      return matches[0].entry;
    }
  }

  return null;
}

// Respuesta por defecto mejorada
export const defaultResponse: KnowledgeEntry = {
  keywords: [],
  category: "Default",
  response: "Lo siento, no entendí completamente tu consulta. 🤔\n\nPuedo ayudarte con:\n\n🛠️ **Soporte técnico**: Problemas de acceso, cargar fotos, visibilidad\n🏠 **Problemas con alojamientos**: Reclamos, servicios, seguridad\n❌ **Cancelaciones y reembolsos**: Políticas y procedimientos\n📋 **Recursos para anfitriones**: Crear y gestionar anuncios\n⭐ **Optimización**: Fotos, precios, reseñas, descripción\n🔒 **Políticas y privacidad**: Términos, responsabilidades\n\n💡 Intenta ser más específico o elige un tema de la lista.",
};
